import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  AutoAwesome,
  Description,
  Pending,
  CheckCircle,
  Edit,
  Visibility,
  Schedule,
  AttachMoney,
  VerifiedUser,
  Assignment,
  CalendarToday,
  Close,
  Security,
  FolderOpen,
  DeleteOutline,
  PlayArrow,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AIMilestoneReportGenerator from './AIMilestoneReportGenerator';
import MilestoneReportApproval from './MilestoneReportApproval';
import { brand } from '../../theme/colors';

/**
 * Enhanced Milestone Manager with AI Report Generation
 * Integrates with existing milestone system
 */
const MilestoneManagerWithReports = ({ 
  projectId, 
  projectContext = {},
  userRole = 'contractor', // 'contractor' or 'client'
  onMilestoneCreated,
  onCreateStagePlan,
  onOpenBOQ,
  refreshKey = 0,
}) => {
  const { getApiClient } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [reports, setReports] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [showReportApproval, setShowReportApproval] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [showMilestoneDetail, setShowMilestoneDetail] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [reportMode, setReportMode] = useState('stage_completion');
  // A contractor's own unsent draft being resumed in the generator.
  const [editingReport, setEditingReport] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchMilestones();
      fetchReports();
    }
  }, [projectId, getApiClient, refreshKey]);

  const fetchMilestones = async () => {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/projects/${projectId}/milestones`);
      setMilestones(response.data.data || []);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/projects/${projectId}/milestone-reports`);
      const allReports = response.data.data || [];
      setReports(allReports);
      
      // Filter pending reports for client
      if (userRole === 'client') {
        const pending = allReports.filter(r => 
          ['submitted', 'under_review'].includes(r.status)
        );
        setPendingReports(pending);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const handleReportCreated = (report) => {
    fetchReports();
    setShowReportGenerator(false);
  };

  const handleReportApproved = (report) => {
    fetchMilestones();
    fetchReports();
    setShowReportApproval(false);
  };

  const handleViewReport = async (report) => {
    const reportId = typeof report === 'object' ? report.id : report;
    const status = typeof report === 'object' ? report.status : null;
    // The contractor's own unsent draft reopens in the form, where they left off.
    if (userRole !== 'client' && status === 'draft') {
      try {
        const res = await getApiClient().get(`/milestone-reports/${reportId}`);
        const full = res.data?.data;
        setEditingReport(full);
        setReportMode(full?.report_type || 'stage_completion');
        setShowReportGenerator(true);
        return;
      } catch (_) {
        // fall through to the read-only view
      }
    }
    setSelectedReportId(reportId);
    setShowReportApproval(true);
  };

  const handleViewMilestoneDetail = (milestone) => {
    setSelectedMilestone(milestone);
    setShowMilestoneDetail(true);
  };

  const canDeleteStage = (milestone) => {
    if (!milestone) return false;
    if (userRole !== 'contractor' && userRole !== 'client' && userRole !== 'admin') return false;
    const status = milestone.status || 'not_started';
    const progress = Number(milestone.progress || 0);
    if (!['not_started', 'pending_approval'].includes(status)) return false;
    if (progress > 0) return false;
    if (milestone.created_from_report_id) return false;
    return true;
  };

  const requestDelete = (milestone) => {
    setDeleteError(null);
    setDeleteTarget(milestone);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const apiClient = getApiClient();
      await apiClient.delete(`/milestones/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchMilestones();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to delete stage';
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try { return new Date(dateStr).toLocaleDateString(); } catch { return 'Invalid'; }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getMilestoneStatusColor = (status) => {
    const colors = {
      not_started: 'default',
      in_progress: 'primary',
      completed: 'success',
      verified: 'success',
      failed: 'error',
      pending_approval: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      under_review: 'warning',
      approved: 'success',
      changes_requested: 'warning',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      under_review: 'Under Review',
      approved: 'Approved',
      changes_requested: 'Changes Requested',
      rejected: 'Rejected',
    };
    return labels[status] || status;
  };

  const completedStages = milestones.filter(m => ['completed', 'verified', 'released'].includes(m.status)).length;
  const submittedReports = reports.filter(r => ['submitted', 'under_review'].includes(r.status)).length;
  const hasStages = milestones.length > 0;
  const isUnstarted = (m) => (m.status || 'not_started') === 'not_started' && Number(m.progress || 0) === 0;
  const notStartedStages = milestones.filter(isUnstarted);
  const [startingId, setStartingId] = useState(null);
  const [startError, setStartError] = useState(null);

  // "Start this stage": the contractor's record that work has begun. Sets the
  // stage in_progress with today's actual start date (server also refuses if
  // the stage isn't funded). Until now the contractor had no way to do this
  // — funded-start reminders kept firing on a stage he could not mark started.
  const startStage = async (milestone) => {
    if (!milestone || startingId) return;
    setStartingId(milestone.id);
    setStartError(null);
    try {
      const apiClient = getApiClient();
      await apiClient.put(`/milestones/${milestone.id}/progress`, { progress: 5 });
      await fetchMilestones();
    } catch (err) {
      setStartError(err?.response?.data?.message || 'Could not start this stage. Please try again.');
    } finally {
      setStartingId(null);
    }
  };

  const openStagePlan = () => {
    if (onCreateStagePlan) {
      onCreateStagePlan();
      return;
    }
    setShowReportGenerator(true);
  };

  const openCompletionReport = (mode = 'stage_completion') => {
    setReportMode(mode);
    setShowReportGenerator(true);
  };

  const getContractorGuidance = () => {
    if (!hasStages) {
      return {
        severity: 'info',
        title: 'Next: propose the work stages',
        body: 'Break this project into stages such as siting, excavation and foundation, roofing, plumbing, electrical, finishes, and final handover. For specialist jobs, create one stage and describe the work clearly.',
        primary: 'Create Stage Plan',
        primaryAction: openStagePlan,
        secondary: 'Open BOQ',
        secondaryAction: onOpenBOQ,
      };
    }

    if (submittedReports === 0 && notStartedStages.length === milestones.length) {
      const first = notStartedStages[0];
      return {
        severity: 'info',
        title: `Start "${first.milestoneDetails?.name || first.name || first.title || 'the first stage'}" and keep the owner updated`,
        body: 'Press Start this stage the day you begin, then post a short Daily Update with a photo or two. When the stage is done, submit a Completion Report for the owner to approve and release payment.',
        primary: startingId ? 'Starting…' : 'Start this stage',
        primaryAction: () => startStage(first),
        secondary: 'Open BOQ',
        secondaryAction: onOpenBOQ,
      };
    }

    if (submittedReports > 0) {
      return {
        severity: 'warning',
        title: `${submittedReports} report${submittedReports > 1 ? 's' : ''} awaiting owner review`,
        body: 'The owner needs to approve or request changes before payment can be released. Use messages if you need to clarify anything.',
        primary: 'View Reports',
        primaryAction: () => setActiveTab(1),
      };
    }

    if (completedStages < milestones.length) {
      return {
        severity: 'success',
        title: 'Work through each approved stage',
        body: 'Keep the BOQ current, complete the stage, then submit a Completion Report with photos, costs, and any certificates or notes the owner should review.',
        primary: 'Submit Completion Report',
        primaryAction: () => openCompletionReport('stage_completion'),
        secondary: 'Open BOQ',
        secondaryAction: onOpenBOQ,
      };
    }

    return {
      severity: 'success',
      title: 'Ready for final handover',
      body: 'Create the final report with completed work, handover documents, certificates, photos, sign-off notes, and the final payment release request.',
      primary: 'Submit Final Report',
      primaryAction: () => openCompletionReport('final_handover'),
    };
  };

  const contractorGuidance = userRole === 'contractor' ? getContractorGuidance() : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Work Stages & Payment Progress</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Plan the stages, agree the BOQ, complete work, then submit reports for owner approval and payment release.
          </Typography>
        </Box>
        {userRole === 'contractor' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={openStagePlan}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Add Stage
            </Button>
            <Button
              variant="contained"
              startIcon={<Description />}
              onClick={() => openCompletionReport('stage_completion')}
              sx={{ background: brand.gradient, textTransform: 'none', fontWeight: 700 }}
            >
              Completion Report
            </Button>
          </Box>
        )}
      </Box>

      {startError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStartError(null)}>{startError}</Alert>
      )}
      {contractorGuidance && (
        <Alert
          severity={contractorGuidance.severity}
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {contractorGuidance.secondary && contractorGuidance.secondaryAction && (
                <Button color="inherit" size="small" onClick={contractorGuidance.secondaryAction}>
                  {contractorGuidance.secondary}
                </Button>
              )}
              <Button color="inherit" size="small" onClick={contractorGuidance.primaryAction}>
                {contractorGuidance.primary}
              </Button>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{contractorGuidance.title}</Typography>
          <Typography variant="body2">{contractorGuidance.body}</Typography>
        </Alert>
      )}

      {/* Pending Reports Alert (for clients) */}
      {userRole === 'client' && pendingReports.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => setActiveTab(1)}
            >
              Review Now
            </Button>
          }
        >
          You have {pendingReports.length} completion report{pendingReports.length > 1 ? 's' : ''} pending your approval
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle fontSize="small" />
              Work Stages ({milestones.length})
            </Box>
          } 
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description fontSize="small" />
              Completion Reports ({reports.length})
              {pendingReports.length > 0 && (
                <Badge badgeContent={pendingReports.length} color="error" />
              )}
            </Box>
          } 
        />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {milestones.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No work stages approved yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {userRole === 'contractor' 
                  ? 'Create a Stage Plan, add BOQ/costs, and send it to the owner for approval before starting work.'
                  : 'Waiting for the contractor to propose work stages'}
              </Typography>
              {userRole === 'contractor' && (
                <Button
                  variant="contained"
                  startIcon={<Assignment />}
                  onClick={openStagePlan}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                  }}
                >
                  Create Stage Plan
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {milestones.map((milestone) => (
                <Grid item xs={12} md={6} key={milestone.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6">
                          {milestone.milestoneDetails?.name || milestone.name}
                        </Typography>
                        <Chip 
                          label={milestone.status} 
                          size="small" 
                          color={milestone.status === 'completed' ? 'success' : 'default'}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {milestone.milestoneDetails?.description || milestone.description}
                      </Typography>

                      {/* Progress bar */}
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {milestone.progress || 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={milestone.progress || 0}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {milestone.created_from_report_id && (
                            <Tooltip title="Created from an approved report">
                              <Chip
                                icon={<AutoAwesome />}
                                label="Report Generated"
                                size="small"
                                color="secondary"
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {canDeleteStage(milestone) && (
                            <Tooltip title="Delete this stage">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestDelete(milestone);
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {userRole === 'contractor' && isUnstarted(milestone) && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<PlayArrow />}
                              disabled={Boolean(startingId)}
                              onClick={(e) => { e.stopPropagation(); startStage(milestone); }}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              {startingId === milestone.id ? 'Starting…' : 'Start this stage'}
                            </Button>
                          )}
                          <Button
                            size="small"
                            endIcon={<Visibility />}
                            onClick={() => handleViewMilestoneDetail(milestone)}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {reports.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No completion reports yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {userRole === 'contractor' 
                  ? hasStages
                    ? 'When a stage is complete, submit a report with photos, costs, deliverables, and any documents needed for owner approval.'
                    : 'Create your Stage Plan first. Completion reports come after a stage has been agreed and work has been done.'
                  : 'No reports have been submitted yet'}
              </Typography>
              {userRole === 'contractor' && (
                <Button
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={hasStages ? () => openCompletionReport('stage_completion') : openStagePlan}
                  sx={{
                    background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.primaryLight} 90%)`,
                  }}
                >
                  {hasStages ? 'Submit Completion Report' : 'Create Stage Plan'}
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {reports.map((report) => (
                <Grid item xs={12} md={6} key={report.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 },
                      border: pendingReports.includes(report) ? '2px solid' : 'none',
                      borderColor: 'warning.main',
                    }}
                    onClick={() => handleViewReport(report)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {report.report_number}
                          </Typography>
                          <Typography variant="h6">
                            {report.milestone_name}
                          </Typography>
                        </Box>
                        <Chip 
                          label={getStatusLabel(report.status)} 
                          size="small" 
                          color={getStatusColor(report.status)}
                        />
                      </Box>

                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {report.milestone_overview || report.scope_of_work}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {report.deliverables && (
                            <Chip 
                              label={`${report.deliverables.length} deliverables`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                          {report.commentCount > 0 && (
                            <Chip 
                              label={`${report.commentCount} comments`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        <Button
                          size="small"
                          endIcon={userRole !== 'client' && report.status === 'draft' ? <Edit /> : <Visibility />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewReport(report);
                          }}
                        >
                          {userRole !== 'client' && report.status === 'draft' ? 'Continue' : 'View'}
                        </Button>
                      </Box>

                      {report.status === 'changes_requested' && report.client_conditions && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography variant="caption">
                            Changes requested: {report.client_conditions}
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Dialogs */}
      <AIMilestoneReportGenerator
        open={showReportGenerator}
        onClose={() => { setShowReportGenerator(false); setEditingReport(null); fetchReports(); }}
        projectId={projectId}
        projectContext={projectContext}
        reportMode={reportMode}
        initialReport={editingReport}
        onReportCreated={handleReportCreated}
        onDraftDeleted={() => { setEditingReport(null); fetchReports(); }}
      />

      <MilestoneReportApproval
        open={showReportApproval}
        onClose={() => {
          setShowReportApproval(false);
          setSelectedReportId(null);
        }}
        reportId={selectedReportId}
        userRole={userRole}
        onApproved={handleReportApproved}
        onChangesRequested={() => {
          fetchReports();
          setShowReportApproval(false);
        }}
        onDeleted={() => {
          fetchReports();
          setSelectedReportId(null);
        }}
        onRejected={() => {
          fetchReports();
          setShowReportApproval(false);
        }}
      />

      {/* Work Stage Detail Dialog */}
      <Dialog
        open={showMilestoneDetail}
        onClose={() => setShowMilestoneDetail(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedMilestone && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {selectedMilestone.milestoneDetails?.name || selectedMilestone.name}
                  </Typography>
                  {selectedMilestone.milestoneDetails?.type && (
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {selectedMilestone.milestoneDetails.type.replace(/_/g, ' ')} Stage
                      {selectedMilestone.milestoneDetails?.order ? ` • #${selectedMilestone.milestoneDetails.order}` : ''}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={(selectedMilestone.status || 'unknown').replace(/_/g, ' ')}
                    size="small"
                    color={getMilestoneStatusColor(selectedMilestone.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <IconButton size="small" onClick={() => setShowMilestoneDetail(false)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Progress */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>Progress</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {selectedMilestone.progress || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={selectedMilestone.progress || 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Description */}
              {(selectedMilestone.milestoneDetails?.description || selectedMilestone.description) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {selectedMilestone.milestoneDetails?.description || selectedMilestone.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Timeline */}
              {selectedMilestone.scheduling && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Schedule fontSize="small" color="action" />
                    <Typography variant="subtitle2">Timeline</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Planned Start</Typography>
                      <Typography variant="body2">{formatDate(selectedMilestone.scheduling.plannedStartDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Planned End</Typography>
                      <Typography variant="body2">{formatDate(selectedMilestone.scheduling.plannedEndDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Actual Start</Typography>
                      <Typography variant="body2">{formatDate(selectedMilestone.scheduling.actualStartDate)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Actual End</Typography>
                      <Typography variant="body2">{formatDate(selectedMilestone.scheduling.actualEndDate)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Financial */}
              {selectedMilestone.financial && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Typography variant="subtitle2">Financial</Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Budget Allocation</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(selectedMilestone.financial.budgetAllocation)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Actual Cost</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(selectedMilestone.financial.actualCost)}
                      </Typography>
                    </Grid>
                    {selectedMilestone.financial.budgetAllocation > 0 && selectedMilestone.financial.actualCost > 0 && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Variance</Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={selectedMilestone.financial.actualCost <= selectedMilestone.financial.budgetAllocation ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(selectedMilestone.financial.budgetAllocation - selectedMilestone.financial.actualCost)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Payment Schedule */}
                  {selectedMilestone.financial.paymentSchedule?.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Payment Schedule
                      </Typography>
                      {selectedMilestone.financial.paymentSchedule.map((tranche, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.75,
                            px: 1.5,
                            mb: 0.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              {tranche.percentage}% — {(tranche.trigger || '').replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(tranche.amount)}
                            </Typography>
                            <Chip
                              label={tranche.status || 'pending'}
                              size="small"
                              color={tranche.status === 'released' ? 'success' : 'default'}
                              sx={{ textTransform: 'capitalize', height: 22, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Requirements */}
              {selectedMilestone.requirements && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Assignment fontSize="small" color="action" />
                    <Typography variant="subtitle2">Requirements</Typography>
                  </Box>

                  {selectedMilestone.requirements.verificationScope?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Verification Scope
                      </Typography>
                      <List dense disablePadding>
                        {selectedMilestone.requirements.verificationScope.map((item, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircle fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {selectedMilestone.requirements.qualityChecks?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Quality Checks
                      </Typography>
                      <List dense disablePadding>
                        {selectedMilestone.requirements.qualityChecks.map((item, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <VerifiedUser fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {selectedMilestone.requirements.safetyRequirements?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Safety Requirements
                      </Typography>
                      <List dense disablePadding>
                        {selectedMilestone.requirements.safetyRequirements.map((item, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Security fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {selectedMilestone.requirements.documentRequirements?.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Document Requirements
                      </Typography>
                      <List dense disablePadding>
                        {selectedMilestone.requirements.documentRequirements.map((item, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <FolderOpen fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}

              {/* Verification Status */}
              {selectedMilestone.verification && selectedMilestone.verification.required && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <VerifiedUser fontSize="small" color="action" />
                      <Typography variant="subtitle2">Verification</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box>
                          <Chip
                            label={(selectedMilestone.verification.status || 'pending').replace(/_/g, ' ')}
                            size="small"
                            color={
                              selectedMilestone.verification.status === 'completed' ? 'success' :
                              selectedMilestone.verification.status === 'failed' ? 'error' : 'default'
                            }
                            sx={{ textTransform: 'capitalize', mt: 0.5 }}
                          />
                        </Box>
                      </Grid>
                      {selectedMilestone.verification.score != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Score</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {selectedMilestone.verification.score}/100
                          </Typography>
                        </Grid>
                      )}
                      {selectedMilestone.verification.passed != null && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Result</Typography>
                          <Box>
                            <Chip
                              label={selectedMilestone.verification.passed ? 'Passed' : 'Failed'}
                              size="small"
                              color={selectedMilestone.verification.passed ? 'success' : 'error'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Grid>
                      )}
                      {selectedMilestone.verification.completedAt && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Completed</Typography>
                          <Typography variant="body2">{formatDate(selectedMilestone.verification.completedAt)}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowMilestoneDetail(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Stage Confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this stage?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{deleteTarget?.milestoneDetails?.name || deleteTarget?.name}</strong> will be removed from this project. This cannot be undone.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Only stages that have not been started and have no approved completion report can be deleted.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={<DeleteOutline />}
          >
            {deleting ? 'Deleting…' : 'Delete Stage'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MilestoneManagerWithReports;
